import { CheckIcon } from "@xola/icons";
import { flash, Label, Modal, Select, SubmitButton, Table, Textarea } from "@xola/ui-kit";
import { useDebounceFn } from "ahooks";
import clsx from "clsx";
import { QuantitySpinner } from "common";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { LineItem, Purchase } from "types";
import { getInitialDamageMap, getReportDamagePayload } from "./merchandiseFulfillmentHelper";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { AdjustLotResponse, useAdjustInventoryLot } from "@/hooks/resources/useAdjustInventoryLot";
import { useInventoryLots } from "@/hooks/resources/useInventoryLots";
import { useInvalidate } from "@/hooks/useInvalidation";
import {
    getInventoryEnabledMerchandisePurchaseItems,
    getMerchandiseWareLineItems,
} from "@/models/MerchandisePurchaseItem";

const DAMAGE_REASONS = [
    "Found Damaged",
    "Quality Control",
    "Theft/Loss",
    "Promotion/Donation",
    "Permanently Removed",
    "Other",
] as const;

const getInventoryLookupKey = (productId: ID | undefined, variantLabel: string | undefined) => {
    return `${productId ?? ""}_${variantLabel ?? ""}`;
};

type RowStatus = { type: "success" } | { type: "error"; message: string };

interface ReportDamagedMerchandiseModalProps {
    purchase: Purchase;
    isOpen: boolean;
    onClose: () => void;
}

export const ReportDamagedMerchandiseModal = ({ purchase, isOpen, onClose }: ReportDamagedMerchandiseModalProps) => {
    return (
        <Modal isOpen={isOpen} size="medium" onClose={onClose}>
            <ReportDamagedMerchandiseModalContent purchase={purchase} onClose={onClose} />
        </Modal>
    );
};

const ReportDamagedMerchandiseModalContent = ({
    purchase,
    onClose,
}: Omit<ReportDamagedMerchandiseModalProps, "isOpen">) => {
    const invalidate = useInvalidate();
    const [damagedMap, setDamagedMap] = useState<Record<string, number>>({});
    const [reason, setReason] = useState<string>(DAMAGE_REASONS[0]);
    const [comment, setComment] = useState("");
    const [rowStatuses, setRowStatuses] = useState<Record<string, RowStatus>>({});

    useEffect(() => {
        setDamagedMap(getInitialDamageMap(purchase));
    }, [purchase]);

    const { mutateAsync: adjustLot, isLoading } = useAdjustInventoryLot();
    const successIdsRef = useRef<string[]>([]);
    const { run: triggerClose } = useDebounceFn(onClose, { wait: 3000 });
    const { run: triggerClearSuccess } = useDebounceFn(
        () => {
            const ids = successIdsRef.current;
            setRowStatuses((previous) =>
                Object.fromEntries(
                    Object.entries(previous).filter(([id, s]) => !(ids.includes(id) && s.type === "success")),
                ),
            );
        },
        { wait: 3000 },
    );
    const merchandiseItems = useMemo(() => getInventoryEnabledMerchandisePurchaseItems(purchase), [purchase]);

    const products = useMemo(
        () => merchandiseItems.map((item) => ({ id: item.product.id, name: item.name })),
        [merchandiseItems],
    );
    const { data: inventoryRows, isLoading: inventoryLoading } = useInventoryLots(products);

    const inventoryRowByKey = useMemo(() => {
        const map: Record<string, typeof inventoryRows[number]> = {};
        for (const row of inventoryRows) {
            map[getInventoryLookupKey(row.productId, row.variantLabel)] = row;
        }
        return map;
    }, [inventoryRows]);

    // Map each line item's template to its WareInventoryLot.id via variant label match
    const templateToLotId = useMemo(() => {
        const map: Record<string, string> = {};
        for (const item of merchandiseItems) {
            for (const lineItem of getMerchandiseWareLineItems(item)) {
                const templateId = lineItem.template?.id;
                if (!templateId) continue;
                const row = inventoryRowByKey[getInventoryLookupKey(item.product?.id, lineItem.name)];
                if (row) {
                    map[templateId] = row.id;
                }
            }
        }
        return map;
    }, [merchandiseItems, inventoryRowByKey]);

    const handleSubmit = async () => {
        const result = getReportDamagePayload(purchase, damagedMap, reason, templateToLotId, comment || undefined);
        if (!result) {
            flash.danger("Unable to report damage for the selected items");
            return;
        }
        const { ops, opToLineItem } = result;
        setRowStatuses({});

        try {
            const response: AdjustLotResponse = await adjustLot({
                errorStrategy: "continue_on_failure",
                ops,
                productId: merchandiseItems[0]?.product?.id,
            });
            const newStatuses: Record<string, RowStatus> = {};
            const successIds: string[] = [];

            for (const opResult of response?.results ?? []) {
                const lineItemId = opToLineItem[opResult.id];
                if (!lineItemId) continue;

                if (opResult.status === "success") {
                    newStatuses[lineItemId] = { type: "success" };
                    successIds.push(lineItemId);
                } else {
                    newStatuses[lineItemId] = {
                        type: "error",
                        message: opResult.error?.message ?? "An error occurred",
                    };
                }
            }

            setRowStatuses(newStatuses);
            invalidate("purchases");

            const hasErrors = Object.values(newStatuses).some((s) => s.type === "error");

            if (!hasErrors) {
                triggerClose();
            } else {
                successIdsRef.current = successIds;
                triggerClearSuccess();
            }
        } catch {
            flash.danger("Failed to report damage");
        }
    };

    const handleDamagedChange = (lineItemId: string, value: number) => {
        setDamagedMap((previous) => ({ ...previous, [lineItemId]: value }));
    };

    const hasChanges = Object.values(damagedMap).some((v) => v > 0);

    return (
        <>
            <Modal.Header onClose={onClose}>
                Report Damaged Items
                <p className="mt-1 text-sm font-normal text-gray-dark">
                    This action can only be undone by an Inventory Manager
                </p>
            </Modal.Header>
            <Modal.Body>
                <div className="overflow-hidden rounded-lg border border-gray-lighter">
                    <Table className="w-full table-fixed border-none">
                        <Table.Head>
                            <Table.Row>
                                <Table.Header className="text-left">Item</Table.Header>
                                <Table.Header className="w-44 text-left">Quantity Damaged</Table.Header>
                            </Table.Row>
                        </Table.Head>
                    </Table>
                    <div className="max-h-64 overflow-y-auto" data-testid="report-damaged-table">
                        <Table className="w-full table-fixed border-none">
                            <Table.Body>
                                {merchandiseItems.map((item) =>
                                    getMerchandiseWareLineItems(item).map((lineItem: LineItem) => {
                                        const status = rowStatuses[lineItem.id];
                                        const isError = status?.type === "error";
                                        const isSuccess = status?.type === "success";
                                        return (
                                            <React.Fragment key={lineItem.id}>
                                                <Table.Row>
                                                    <Table.Cell className="!whitespace-normal">
                                                        <div
                                                            className={clsx(
                                                                "break-words font-bold",
                                                                isError && "text-danger",
                                                            )}
                                                        >
                                                            {item.name}
                                                        </div>
                                                        <div
                                                            className={clsx(
                                                                "break-words text-sm",
                                                                isError ? "text-danger" : "text-gray-dark",
                                                            )}
                                                        >
                                                            {lineItem.name}
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className="w-44">
                                                        <div className="flex items-center gap-2">
                                                            {isSuccess && (
                                                                <CheckIcon
                                                                    size="small"
                                                                    className="shrink-0 text-success"
                                                                />
                                                            )}
                                                            <QuantitySpinner
                                                                allowEditManually
                                                                value={damagedMap[lineItem.id] ?? 0}
                                                                min={0}
                                                                minErrorMessage="Cannot damage a negative quantity"
                                                                max={
                                                                    inventoryRowByKey[
                                                                        getInventoryLookupKey(
                                                                            item.product?.id,
                                                                            lineItem.name,
                                                                        )
                                                                    ]?.onHand ?? 0
                                                                }
                                                                disabled={isLoading}
                                                                onChange={(value) =>
                                                                    handleDamagedChange(lineItem.id, value)
                                                                }
                                                            />
                                                        </div>
                                                    </Table.Cell>
                                                </Table.Row>
                                                {isError && (
                                                    <Table.Row>
                                                        <Table.Cell colSpan={2} className="!pt-0 text-sm text-danger">
                                                            {(status as { type: "error"; message: string }).message}
                                                        </Table.Cell>
                                                    </Table.Row>
                                                )}
                                            </React.Fragment>
                                        );
                                    }),
                                )}
                            </Table.Body>
                        </Table>
                    </div>
                </div>

                <div className="mt-4 space-y-3">
                    <div>
                        <Label className="mb-1 block font-bold">Reason</Label>
                        <Select
                            data-testid="report-damaged-reason"
                            value={reason}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setReason(e.target.value)}
                        >
                            {DAMAGE_REASONS.map((r) => (
                                <option key={r} value={r}>
                                    {r === DAMAGE_REASONS[0] ? `${r} (default)` : r}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Label className="mb-1 block font-bold">Comments</Label>
                        <Textarea
                            data-testid="report-damaged-comments"
                            value={comment}
                            placeholder="Add any additional notes..."
                            className="w-full"
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="flex justify-end">
                <div className="flex space-x-2">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <SubmitButton
                        isLoading={isLoading}
                        disabled={!hasChanges || isLoading || inventoryLoading}
                        onClick={handleSubmit}
                    >
                        Apply
                    </SubmitButton>
                </div>
            </Modal.Footer>
        </>
    );
};
